import { isOptionFieldType } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import {
  CLIENT_FIELD_APPLIES_TO_ALL,
  type ClientFieldApplicability,
  type CustomFieldOptionInput,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TranslationButton } from "@/components/ui/translation-button";
import { patch, post } from "@/lib/api/_client";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./custom-field-dialog.i18n";
import type { ClientTab, CustomField, CustomFieldType } from "./settings-types";

type CustomFieldDialogProps = {
  open: boolean;
  field: CustomField | null;
  clientTabs?: ClientTab[];
  supportedLanguages?: string[];
  onClose: () => void;
};

export function CustomFieldDialog({
  open,
  field,
  clientTabs = [],
  supportedLanguages = ["en_GB"],
  onClose,
}: CustomFieldDialogProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  // Each option row splits its translations the way the name does: `label` is
  // the text in the user's language, `labelI18n` holds the other languages
  // (edited via TranslationButton, same as the other settings tables).
  // Existing options keep their id so label edits stay in place (QL-123);
  // `key` is a stable local identity for React keys.
  type OptionRow = { key: string; id?: string; label: string; labelI18n: LocalizedString };
  const [formData, setFormData] = useState<{
    name: string;
    nameI18n: LocalizedString;
    type: CustomFieldType;
    options: OptionRow[];
    visibleInList: boolean;
    visibleInProfile: boolean;
    clientType: ClientFieldApplicability;
    tabId: string | null;
  }>({
    name: "",
    nameI18n: {},
    type: "text",
    options: [],
    visibleInList: true,
    visibleInProfile: true,
    clientType: CLIENT_FIELD_APPLIES_TO_ALL,
    tabId: null,
  });
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (field) {
      const options: OptionRow[] = isOptionFieldType(field.type)
        ? (field.options ?? [])
            .filter((option) => !option.isArchived)
            .map((option) => {
              const labels = option.labelI18n || {};
              return {
                key: option.id,
                id: option.id,
                label: labels[userLanguage] || Object.values(labels)[0] || "",
                labelI18n: Object.fromEntries(
                  Object.entries(labels).filter(([lang]) => lang !== userLanguage),
                ),
              };
            })
        : [];
      // Get the value for user's language from nameI18n, with fallback to any available value
      const i18n = field.nameI18n || {};
      const nameInUserLang =
        i18n[userLanguage as keyof typeof i18n] || Object.values(i18n)[0] || "";

      // Other translations (excluding user's language) go in nameI18n for the dialog
      const otherTranslations = Object.fromEntries(
        Object.entries(i18n).filter(([lang]) => lang !== userLanguage),
      );

      setFormData({
        name: nameInUserLang,
        nameI18n: otherTranslations,
        type: field.type as CustomFieldType,
        options,
        visibleInList: field.visibleInList,
        visibleInProfile: field.visibleInProfile,
        clientType: field.clientType || CLIENT_FIELD_APPLIES_TO_ALL,
        tabId: field.tabId || null,
      });
    } else {
      setFormData({
        name: "",
        nameI18n: {},
        type: "text",
        options: [],
        visibleInList: true,
        visibleInProfile: true,
        clientType: CLIENT_FIELD_APPLIES_TO_ALL,
        tabId: null,
      });
    }
    setError(null);
  }, [field, open, userLanguage]);

  const handleAddOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { key: crypto.randomUUID(), label: "", labelI18n: {} }],
    });
    setError(null);
  };

  const handleRemoveOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    });
  };

  // Swap with the neighbour; the saved order is simply the row order.
  const handleMoveOption = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= formData.options.length) return;
    const options = [...formData.options];
    [options[index], options[target]] = [options[target]!, options[index]!];
    setFormData({ ...formData, options });
  };

  const handleOptionChange = (index: number, patch: Partial<OptionRow>) => {
    setFormData({
      ...formData,
      options: formData.options.map((option, i) =>
        i === index ? { ...option, ...patch } : option,
      ),
    });
  };

  const handleTypeChange = (type: CustomFieldType) => {
    setFormData({
      ...formData,
      type,
      options: isOptionFieldType(type) ? formData.options : [],
    });
  };

  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isOptionFieldType(formData.type) && formData.options.length === 0) {
      setError(t.optionsRequired);
      return;
    }

    const hasEmptyOption =
      isOptionFieldType(formData.type) &&
      formData.options.some(
        (option) =>
          !option.label.trim() && !Object.values(option.labelI18n).some((label) => label?.trim()),
      );
    if (hasEmptyOption) {
      setError(t.optionLabelRequired);
      return;
    }

    startTransition(async () => {
      try {
        // Merge the user's-language label back into labelI18n (same split
        // LocalizedInput uses for the field name).
        const options: CustomFieldOptionInput[] = formData.options.map((option) => ({
          ...(option.id ? { id: option.id } : {}),
          labelI18n: option.label.trim()
            ? { ...option.labelI18n, [userLanguage]: option.label }
            : { ...option.labelI18n },
        }));
        const dataWithLanguage = { ...formData, options, userLanguage };
        const url = field ? `/internal/custom-fields/${field.id}` : "/internal/custom-fields";
        if (field) {
          await patch(url, dataWithLanguage);
        } else {
          await post(url, dataWithLanguage);
        }

        queryClient.invalidateQueries({ queryKey: ["customFields"] });
        onClose();
      } catch (err: any) {
        setError(err.message || t.failedToSave);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{field ? t.editCustomField : t.addNewCustomField}</DialogTitle>
          <DialogDescription>
            {field ? t.updateFieldDescription : t.createFieldDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {t.fieldName} <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t.fieldNamePlaceholder}
                  required
                  className="flex-1"
                />
                <TranslationButton
                  value={{ ...formData.nameI18n, [userLanguage]: formData.name } as LocalizedString}
                  onSave={(nameI18n) => {
                    const { [userLanguage]: own, ...rest } = nameI18n as Record<string, string>;
                    setFormData({ ...formData, name: own ?? formData.name, nameI18n: rest });
                  }}
                  supportedLanguages={supportedLanguages}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">
                {t.fieldType} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value) => handleTypeChange(value as CustomFieldType)}
                disabled={!!field}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.selectFieldType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">{t.typeText}</SelectItem>
                  <SelectItem value="textarea">{t.typeTextarea}</SelectItem>
                  <SelectItem value="number">{t.typeNumber}</SelectItem>
                  <SelectItem value="date">{t.typeDate}</SelectItem>
                  <SelectItem value="datetime">{t.typeDateTime}</SelectItem>
                  <SelectItem value="dropdown">{t.typeDropdown}</SelectItem>
                  <SelectItem value="radio">{t.typeRadio}</SelectItem>
                  <SelectItem value="multiselect">{t.typeMultiselect}</SelectItem>
                  <SelectItem value="boolean">{t.typeBoolean}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {field && t.fieldTypeCannotChange}
                {!field && formData.type === "text" && t.singleLineText}
                {!field && formData.type === "textarea" && t.multiLineText}
                {!field && formData.type === "number" && t.numericInput}
                {!field && formData.type === "date" && t.datePicker}
                {!field && formData.type === "datetime" && t.dateTimePicker}
                {!field && formData.type === "dropdown" && t.predefinedOptions}
                {!field && formData.type === "radio" && t.radioOptions}
                {!field && formData.type === "multiselect" && t.multiselectOptions}
                {!field && formData.type === "boolean" && t.booleanInput}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clientType">
                {t.appliesToLabel} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.clientType}
                onValueChange={(value) => {
                  const newClientType = value as ClientFieldApplicability;
                  setFormData({
                    ...formData,
                    clientType: newClientType,
                    // The list column is only available to an all-types field.
                    visibleInList:
                      newClientType === CLIENT_FIELD_APPLIES_TO_ALL
                        ? formData.visibleInList
                        : false,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.selectClientType} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CLIENT_FIELD_APPLIES_TO_ALL}>{t.allClientTypes}</SelectItem>
                  <SelectItem value="Person">{t.personOnly}</SelectItem>
                  <SelectItem value="Company">{t.companyOnly}</SelectItem>
                  <SelectItem value="Group">{t.groupOnly}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t.whichClientType}</p>
            </div>

            {clientTabs.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="tabId">{t.tabLabel}</Label>
                <Select
                  value={formData.tabId || "none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, tabId: value === "none" ? null : value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectTab} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.defaultTab}</SelectItem>
                    {clientTabs.map((tab) => (
                      <SelectItem key={tab.id} value={tab.id}>
                        {tab.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t.whichTab}</p>
              </div>
            )}

            {isOptionFieldType(formData.type) && (
              <div className="space-y-2">
                <Label>
                  {t.dropdownOptions} <span className="text-destructive">*</span>
                </Label>
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={option.key} className="flex items-center gap-2">
                      <div className="flex items-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={t.moveOptionUp}
                          onClick={() => handleMoveOption(index, -1)}
                          disabled={index === 0}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={t.moveOptionDown}
                          onClick={() => handleMoveOption(index, 1)}
                          disabled={index === formData.options.length - 1}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        value={option.label}
                        onChange={(e) => handleOptionChange(index, { label: e.target.value })}
                        placeholder={`${t.optionLabel} ${index + 1}`}
                        className="flex-1"
                      />
                      <TranslationButton
                        value={
                          { ...option.labelI18n, [userLanguage]: option.label } as LocalizedString
                        }
                        onSave={(labelI18n) => {
                          const { [userLanguage]: own, ...rest } = labelI18n as Record<
                            string,
                            string
                          >;
                          handleOptionChange(index, {
                            label: own ?? option.label,
                            labelI18n: rest,
                          });
                        }}
                        supportedLanguages={supportedLanguages}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t.removeOption}
                        onClick={() => handleRemoveOption(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleAddOption}>
                  {t.addOption}
                </Button>
                {field && <p className="text-xs text-muted-foreground">{t.optionEditHint}</p>}
              </div>
            )}

            <div className="space-y-3 pt-2 border-t">
              <Label>{t.visibilitySettings}</Label>
              {formData.clientType === CLIENT_FIELD_APPLIES_TO_ALL ? (
                <>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="visibleInList"
                      checked={formData.visibleInList}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          visibleInList: checked === true,
                        })
                      }
                    />
                    <label
                      htmlFor="visibleInList"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {t.visibleInList}
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">{t.showInClientTable}</p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">{t.visibleInListOnlyAllTypes}</p>
              )}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="visibleInProfile"
                  checked={formData.visibleInProfile}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      visibleInProfile: checked === true,
                    })
                  }
                />
                <label
                  htmlFor="visibleInProfile"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t.visibleInProfile}
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-6">{t.showOnClientPage}</p>
              <p className="text-xs text-muted-foreground italic">{t.alwaysVisibleWhenEditing}</p>
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {field ? t.save : t.addCustomField}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
